// Contas puras da aba "Setor dos custos" (sem antd, para o jest do CRA carregar).
import { mesmosSetores, type FracoesSetores } from '../administrativo-plano-acao/paginas/dadosSetoresCusto';

export type OrigemSetor = 'manual' | 'memoria' | 'ia' | 'pessoa_nome' | 'contrato_unico' | 'contrato';
export type Confianca = 'alta' | 'media' | 'baixa';
export type Situacao = 'resolvido' | 'pela_pessoa' | 'pendente';

export interface SugestaoSetor {
  setores: FracoesSetores | null;
  origem: OrigemSetor | null;
  confianca?: Confianca;
  motivo?: string;
  pessoa_sem_setor?: { id: string; nome: string };
  /** setor que o contrato não vende: a margem jogaria na mistura — vem rebaixada (baixa) e nunca entra na carga das altas */
  fora_da_mistura?: boolean;
}

export interface ItemRevisao {
  lancamento_id: string;
  descricao: string;
  data_competencia: string | null;
  periodo: string;
  competencias: string[];
  valor: number | string;
  linha: string;
  grupo?: string;
  fornecedor: { id: string | null; nome: string | null };
  pessoa: { id: string; nome: string; setores: FracoesSetores | null } | null;
  contratos: { id: string; numero: string; titulo: string; mix: FracoesSetores; origem_mix?: string; valor: number | string }[];
  misto: boolean;
  atual: { setores: FracoesSetores | null; origem: OrigemSetor | null; por?: string | null; em?: string | null } | null;
  foto?: { setores: FracoesSetores | null; origem: string | null };
  sugestao: SugestaoSetor | null;
  situacao: Situacao;
  /** false = o lançamento foi apagado depois do fechamento (a carga pula; confirmar responde 404) */
  existe?: boolean;
  /** as linhas da DRE em que virou custo direto (a memória aprende por elas) */
  linhas?: string[];
}

export interface GrupoItens {
  chave: string;
  fornecedor: string;
  linha: string;
  itens: ItemRevisao[];
  valor: number;
  n: number;
}

const SEM_FORNECEDOR = 'Sem fornecedor';

/** Agrupa por fornecedor + linha (a mesma escolha costuma valer para o grupo todo). Mantém a ordem da primeira aparição. */
export function agruparItens(itens: ItemRevisao[]): GrupoItens[] {
  const mapa = new Map<string, GrupoItens>();
  (itens || []).forEach((it) => {
    const fornecedor = it.fornecedor?.nome || SEM_FORNECEDOR;
    const linha = it.linha || '';
    const chave = `${it.fornecedor?.id || fornecedor}|${linha}`;
    let g = mapa.get(chave);
    if (!g) { g = { chave, fornecedor, linha, itens: [], valor: 0, n: 0 }; mapa.set(chave, g); }
    g.itens.push(it);
    g.valor += Number(it.valor) || 0;
    g.n += 1;
  });
  return Array.from(mapa.values());
}

/** Origem a gravar: igual à sugestão -> a da sugestão; "segue o contrato" (vazio) -> 'contrato'; senão 'manual'. */
export function origemDaEscolha(escolha: FracoesSetores | null | undefined, sugestao: SugestaoSetor | null | undefined): OrigemSetor {
  const vazia = !escolha || Object.keys(escolha).length === 0;
  if (vazia) return 'contrato';
  if (sugestao && sugestao.setores && sugestao.origem && sugestao.origem !== 'contrato' && mesmosSetores(escolha, sugestao.setores)) {
    return sugestao.origem;
  }
  return 'manual';
}

export function rotuloSituacao(s: Situacao | string): string {
  if (s === 'resolvido') return 'Resolvido';
  if (s === 'pela_pessoa') return 'Pela pessoa';
  if (s === 'pendente') return 'Pendente';
  return String(s || '—');
}

export function rotuloConfianca(c: Confianca | string | null | undefined): string {
  if (c === 'alta') return 'Confiança alta';
  if (c === 'media') return 'Confiança média';
  if (c === 'baixa') return 'Confiança baixa';
  return '';
}

/** Cor da Tag do antd: alta verde, média laranja, baixa cinza. */
export function corConfianca(c: Confianca | string | null | undefined): string {
  if (c === 'alta') return 'green';
  if (c === 'media') return 'orange';
  return 'default';
}

/** Troca a sugestão só onde a IA respondeu; o resto fica como veio. Não mexe no que está gravado. */
export function aplicarSugestoesIa(
  itens: ItemRevisao[],
  sugestoes: Record<string, { setores: FracoesSetores | null; confianca?: Confianca; motivo?: string; origem?: OrigemSetor }> | null | undefined,
): ItemRevisao[] {
  const s = sugestoes || {};
  return (itens || []).map((it) => {
    const nova = s[it.lancamento_id];
    if (!nova) return it;
    return { ...it, sugestao: { setores: nova.setores, origem: 'ia', confianca: nova.confianca, motivo: nova.motivo } };
  });
}

/** União ordenada e sem repetição das competências alteradas nesta sessão. */
export function competenciasAfetadas(acumulado: string[] | null | undefined, novas: string[] | null | undefined): string[] {
  return Array.from(new Set((acumulado || []).concat(novas || []).filter(Boolean))).sort();
}
